import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const input = "mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";

export default function TeamTab() {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/team").then((r) => setTeam(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(apiError(err)); } };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    await run(() => api.post("/admin/team", form).then(() => setForm({ name: "", email: "", phone: "", password: "" })), "Staff account created");
    setBusy(false);
  };

  const resetPassword = (m) => {
    const pw = window.prompt(`New password for ${m.name} (min 8 characters):`);
    if (pw) run(() => api.patch(`/admin/team/${encodeURIComponent(m.email)}`, { password: pw }), "Password updated");
  };

  if (!team) return <p className="text-sm text-moss">Loading team...</p>;

  return (
    <div data-testid="team-tab">
      <h2 className="font-serif text-3xl font-medium text-forest">Team</h2>
      <p className="mt-1 text-sm text-moss">Staff accounts can manage inventory, sales, bookings, availability, and patient records. Only you can add or remove staff, void sales, and change QuickBooks settings.</p>

      <form onSubmit={create} data-testid="team-form" className="mt-6 rounded-lg border border-linen bg-ivory p-5">
        <h3 className="font-serif text-xl text-forest">Add a staff member</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div><label className={label}>Name</label><input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} /></div>
          <div><label className={label}>Email (their login)</label><input data-testid="team-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} /></div>
          <div><label className={label}>Phone</label><input data-testid="team-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} /></div>
          <div><label className={label}>Temporary password</label><input data-testid="team-password-input" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" className={input} /></div>
        </div>
        <p className="mt-2 text-xs text-moss">Share the password with them privately — they can change it later with "Forgot password?" on the provider login page.</p>
        <button type="submit" data-testid="team-save-button" disabled={busy} className={`${btn} mt-4`}>{busy ? "Creating..." : "Create Account"}</button>
      </form>

      <div className="mt-6 overflow-x-auto rounded-lg border border-linen bg-ivory">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss"><tr><th className="px-4 py-3">Person</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Added</th><th></th></tr></thead>
          <tbody className="divide-y divide-linen">
            {team.map((m) => (
              <tr key={m.email} data-testid={`team-row-${m.email}`}>
                <td className="px-4 py-3"><p className="font-medium text-forest">{m.name}</p><p className="text-xs text-moss">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p></td>
                <td className="px-4 py-3 text-moss">{m.role === "admin" ? "Provider (owner)" : "Staff"}</td>
                <td className="px-4 py-3"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] ${m.active ? "border-forest/30 bg-forest/10 text-forest" : "border-linen text-moss"}`}>{m.active ? "Active" : "Deactivated"}</span></td>
                <td className="px-4 py-3 text-xs text-moss">{(m.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {m.role === "staff" && (
                    <span className="flex justify-end gap-3">
                      <button data-testid={`team-reset-${m.email}`} onClick={() => resetPassword(m)} className="text-moss hover:text-forest" aria-label="Reset password" title="Set new password"><KeyRound size={15} /></button>
                      <button data-testid={`team-toggle-${m.email}`} onClick={() => run(() => api.patch(`/admin/team/${encodeURIComponent(m.email)}`, { active: !m.active }), m.active ? "Account deactivated" : "Account reactivated")} className="text-moss hover:text-forest" aria-label={m.active ? "Deactivate" : "Reactivate"} title={m.active ? "Deactivate" : "Reactivate"}>{m.active ? <UserX size={15} /> : <UserCheck size={15} />}</button>
                      <button data-testid={`team-delete-${m.email}`} onClick={() => window.confirm(`Remove ${m.name}'s account?`) && run(() => api.delete(`/admin/team/${encodeURIComponent(m.email)}`), "Account removed")} className="text-moss hover:text-destructive" aria-label="Remove"><Trash2 size={15} /></button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
